import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Play, Pause, Plus, Check, ArrowLeft, ShieldCheck, Download, Radio, MessageSquareQuote, Share2, Copy } from "lucide-react";
import { api, API, trackCoverUrl, eur, fmtError } from "../lib/api";
import { SEO } from "../components/SEO";
import { usePlayer } from "../context/PlayerContext";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { Stars } from "../components/Reviews";

const TrackDetail = () => {
  const { id } = useParams();
  const [track, setTrack] = useState(null);
  const [error, setError] = useState("");
  const [reviews, setReviews] = useState(null);
  const { current, playing, playTrack } = usePlayer();
  const { add, addExclusive, items } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    setTrack(null);
    setError("");
    api
      .get(`/tracks/${id}`)
      .then((r) => setTrack(r.data))
      .catch((e) => setError(fmtError(e)));
    api.get(`/tracks/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
    // L'URL visible devient l'URL de partage : copiée telle quelle (barre d'adresse),
    // elle affiche la jaquette + titre + artiste dans les publications Facebook/X/LinkedIn.
    window.history.replaceState(null, "", `${API}/share/track/${id}`);
  }, [id]);

  if (error)
    return (
      <main className="flex min-h-screen items-center justify-center" data-testid="track-error">
        <p className="text-sm uppercase tracking-[0.25em] text-zinc-500">{error}</p>
      </main>
    );
  if (!track)
    return (
      <main className="flex min-h-screen items-center justify-center" data-testid="track-loading">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/10 border-t-amber-500" />
      </main>
    );

  const isActive = current?.track_id === track.track_id;
  const inCart = items.some((i) => i.track_id === track.track_id);
  // URL de partage servie par le backend : contient les balises Open Graph
  // (titre + jaquette) lues par Facebook, X, LinkedIn… puis redirige vers cette page.
  const shareUrl = `${API}/share/track/${track.track_id}`;

  const buyExclusive = () => {
    if (items.length && !window.confirm("L'achat d'une exclusivité se paie seul : votre panier actuel sera vidé. Continuer ?")) return;
    addExclusive(track);
    navigate("/panier");
  };

  return (
    <main data-testid="track-detail-page" className="mx-auto max-w-7xl px-5 pb-24 pt-32 sm:px-8">
      {track && (
        <SEO title={`${track.title} — ${track.artist}`} path={`/morceau/${track.track_id}`} type="music.song"
             description={`« ${track.title} » par ${track.artist}${track.genres?.length ? ` (${track.genres.join(", ")})` : ""} — musique en licence pour vos films, pubs et contenus. Écoute et achat en ligne.`.slice(0, 160)}
             image={`${API}/share/track/${track.track_id}/image`} />
      )}
      <Link
        to="/catalogue"
        data-testid="track-back"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-500 transition-colors hover:text-amber-400"
      >
        <ArrowLeft className="h-4 w-4" /> Catalogue
      </Link>

      <div className="mt-10 grid gap-12 lg:grid-cols-2">
        <div className="relative">
          <div className="absolute -inset-4 sm:-inset-10 bg-[radial-gradient(closest-side,rgba(245,158,11,0.14),transparent)]" />
          <img
            src={trackCoverUrl(track)}
            alt={track.title}
            data-testid="track-cover"
            className="relative aspect-square w-full border border-white/10 object-cover shadow-2xl shadow-black"
          />
        </div>

        <div className="flex flex-col justify-center">
          {track.artist_slug ? (
            <Link to={`/artiste/${track.artist_slug}`} data-testid="track-artist-link" className="text-xs uppercase tracking-[0.35em] text-amber-500 transition-colors hover:text-amber-300">
              {track.artist}
            </Link>
          ) : (
            <p className="text-xs uppercase tracking-[0.35em] text-amber-500">{track.artist}</p>
          )}
          <h1 data-testid="track-title" className="mt-3 font-display text-4xl font-black tracking-tighter sm:text-6xl">
            {track.title}
          </h1>
          <div className="mt-6 flex flex-wrap gap-2">
            {(track.usages || []).map((u) => (
              <span key={u} className="border border-amber-500/30 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-amber-400">
                {u}
              </span>
            ))}
            {[...(track.genres || []), ...(track.moods || [])].map((g) => (
              <span key={g} className="border border-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                {g}
              </span>
            ))}
          </div>

          <p className="mt-8 max-w-lg text-sm leading-relaxed text-zinc-400 sm:text-base">{track.description}</p>

          <div className="mt-8 grid grid-cols-3 gap-4 border-y border-white/[0.08] py-6 text-center">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Tempo</p>
              <p className="mt-1 font-display text-xl" data-testid="track-bpm">{track.bpm ? `${track.bpm} BPM` : "—"}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Durée</p>
              <p className="mt-1 font-display text-xl" data-testid="track-duration">{fmtTimeStr(track.duration)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Licence</p>
              <p className="mt-1 font-display text-xl">Unique</p>
            </div>
          </div>

          {track.exclusive_sold ? (
            <div className="mt-8 border border-red-500/30 bg-red-500/5 p-5" data-testid="track-sold-exclusive">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-400">Morceau vendu en exclusivité</p>
              <p className="mt-2 text-sm text-zinc-400">Ce titre a été retiré du catalogue et n'est plus disponible à la licence.</p>
            </div>
          ) : (
            <>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button
                  onClick={() => playTrack(track)}
                  data-testid="track-play"
                  className={`flex h-14 w-14 items-center justify-center rounded-full bg-amber-500 text-black transition-transform hover:scale-105 ${isActive && playing ? "pulse-amber" : ""}`}
                >
                  {isActive && playing ? <Pause className="h-5 w-5" /> : <Play className="ml-0.5 h-5 w-5" />}
                </button>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Licence non exclusive</p>
                  <p data-testid="track-price" className="font-display text-4xl font-black text-amber-500">{eur(track.price)}</p>
                </div>
                <button
                  onClick={() => {
                    if (inCart) return;
                    add(track);
                    toast.success(`« ${track.title} » ajouté au panier`);
                  }}
                  data-testid="track-add-cart"
                  disabled={inCart}
                  className={`ml-auto flex items-center gap-2 px-7 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors ${
                    inCart
                      ? "cursor-default border border-emerald-500/50 text-emerald-400"
                      : "border border-white/20 hover:border-amber-500 hover:bg-amber-500 hover:text-black"
                  }`}
                >
                  {inCart ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  {inCart ? "Dans le panier" : "Ajouter la licence"}
                </button>
              </div>
              {track.exclusive_price > 0 && (
                <div className="mt-4 flex flex-wrap items-center gap-4 border border-amber-500/30 bg-amber-500/5 p-5" data-testid="track-exclusive-block">
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-amber-500">Licence exclusive</p>
                    <p data-testid="track-exclusive-price" className="mt-1 font-display text-2xl font-black text-white">{eur(track.exclusive_price)}</p>
                    <p className="mt-1 text-[11px] leading-relaxed text-zinc-500">
                      Le morceau est retiré du catalogue juste après votre achat — il n'appartiendra qu'à vous.
                    </p>
                  </div>
                  <button
                    onClick={buyExclusive}
                    data-testid="track-buy-exclusive"
                    className="flex items-center gap-2 bg-amber-500 px-7 py-4 text-xs font-bold uppercase tracking-[0.2em] text-black transition-colors hover:bg-amber-400"
                  >
                    Acheter l'exclusivité
                  </button>
                </div>
              )}
            </>
          )}

          <div className="mt-10 space-y-3 text-xs leading-relaxed text-zinc-500">
            <p className="flex items-start gap-3">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-amber-500/70" />
              Licence de synchronisation non exclusive pour un projet (publicité, film ou production d'artiste).
            </p>
            <p className="flex items-start gap-3">
              <Download className="mt-0.5 h-4 w-4 shrink-0 text-amber-500/70" />
              Fichier master haute qualité téléchargeable immédiatement après paiement.
            </p>
            <p className="flex items-start gap-3">
              <Radio className="mt-0.5 h-4 w-4 shrink-0 text-amber-500/70" />
              <span>
                Besoin d'une exclusivité ou d'une déclinaison ?{" "}
                <Link to="/contact" data-testid="track-contact-link" className="text-amber-400 underline-offset-4 transition-colors hover:underline">
                  Contactez le studio
                </Link>
              </span>
            </p>
          </div>

          <div className="mt-8 flex items-center gap-3 border-t border-white/[0.08] pt-6" data-testid="share-row">
            <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Partager</span>
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`${track.title} — ${track.artist} · musique libre de droit`)}&url=${encodeURIComponent(shareUrl)}`}
              target="_blank" rel="noopener noreferrer"
              data-testid="share-x"
              className="flex h-9 w-9 items-center justify-center border border-white/15 text-zinc-400 transition-colors hover:border-amber-500 hover:text-amber-400"
              title="Partager sur X"
            >
              <Share2 className="h-4 w-4" />
            </a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              target="_blank" rel="noopener noreferrer"
              data-testid="share-facebook"
              className="flex h-9 w-9 items-center justify-center border border-white/15 text-zinc-400 transition-colors hover:border-amber-500 hover:text-amber-400"
              title="Partager sur Facebook"
            >
              <span className="text-xs font-bold">f</span>
            </a>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
              target="_blank" rel="noopener noreferrer"
              data-testid="share-linkedin"
              className="flex h-9 w-9 items-center justify-center border border-white/15 text-zinc-400 transition-colors hover:border-amber-500 hover:text-amber-400"
              title="Partager sur LinkedIn"
            >
              <span className="text-xs font-bold">in</span>
            </a>
            <button
              onClick={() => {
                navigator.clipboard.writeText(shareUrl);
                toast.success("Lien copié !");
              }}
              data-testid="share-copy"
              className="flex h-9 w-9 items-center justify-center border border-white/15 text-zinc-400 transition-colors hover:border-amber-500 hover:text-amber-400"
              title="Copier le lien"
            >
              <Copy className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2" data-testid="share-link-field">
            <input
              readOnly
              value={shareUrl}
              onFocus={(e) => e.target.select()}
              data-testid="share-link-input"
              className="min-w-0 flex-1 border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-200/90 outline-none"
            />
            <button
              onClick={() => {
                navigator.clipboard.writeText(shareUrl);
                toast.success("Lien de partage copié — collez-le tel quel dans Facebook");
              }}
              data-testid="share-link-copy"
              className="shrink-0 border border-amber-500/60 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.15em] text-amber-400 transition-colors hover:bg-amber-500 hover:text-black"
            >
              Copier ce lien
            </button>
          </div>
          <p className="mt-2 text-[10px] leading-relaxed text-zinc-600">
            Pour une publication Facebook avec la jaquette et le titre : collez ce lien (et non l'adresse de la page).
          </p>
        </div>
      </div>

      {reviews && reviews.count > 0 && (
        <section className="mt-16 border-t border-white/[0.08] pt-10" data-testid="reviews-section">
          <h2 className="flex items-center gap-3 font-display text-2xl font-medium">
            <MessageSquareQuote className="h-5 w-5 text-amber-500" />
            Avis des acheteurs
            <span className="flex items-center gap-2 text-sm text-zinc-400">
              <Stars value={reviews.avg} /> {reviews.avg}/5 ({reviews.count} avis)
            </span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {reviews.reviews.map((r, i) => (
              <div key={i} className="border border-white/[0.08] bg-surface p-5">
                <div className="flex items-center justify-between">
                  <Stars value={r.rating} />
                  <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">{r.user_name || "Acheteur"}</span>
                </div>
                {r.comment && <p className="mt-3 text-sm leading-relaxed text-zinc-400">{r.comment}</p>}
              </div>
            ))}
          </div>
        </section>
      )}
    </main>
  );
};

const fmtTimeStr = (s) => {
  if (!s) return "—";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

export default TrackDetail;
